import React, { useRef, useState } from "react";
import gsap from "gsap";
import { useTranslation } from "react-i18next";
import api from "../axios";

const FIELD_CLASS =
  "w-full rounded-lg border border-ink-200 bg-white px-4 py-3 text-base text-ink-900 placeholder-ink-400 " +
  "focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-colors";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const successRef = useRef(null);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };
  


  
  const playSuccess = () => {
    const el = successRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      gsap.set(el, { opacity: 1, scale: 1 });
      return;
    }
    gsap.fromTo(
      el,
      { opacity: 0, scale: 0.85 },
      { opacity: 1, scale: 1, duration: 0.45, ease: "back.out(2)" }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
      requestAnimationFrame(playSuccess);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <main className="bg-white">
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-5 gap-14">
        <div className="md:col-span-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
            {t("Contact")}
          </p>
          <h1 className="mt-3 font-display font-extrabold text-3xl text-ink-900">
            {t("Talk to us")}
          </h1>
          <p className="mt-4 text-ink-500 leading-relaxed">
            {t("Questions, feedback, or a bug to report — we read every message.")}
          </p>

          <dl className="mt-10 space-y-5 text-sm">
            <div>
              <dt className="text-ink-400">{t("Email")}</dt>
              <dd>
                <a
                  href="mailto:support@linkakam.com"
                  className="font-semibold text-ink-800 hover:text-brand-600"
                >
                  support@linkakam.com
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-ink-400">{t("Phone")}</dt>
              <dd>
                <a
                  href="tel:+9647737330003"
                  className="font-semibold text-ink-800 hover:text-brand-600"
                >
                  +964 773 733 0003
                </a>
              </dd>
            </div>
          </dl>
        </div>

        <div className="md:col-span-3">
          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-ink-700 mb-1.5">
                  {t("Name")}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={onChange}
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink-700 mb-1.5">
                  {t("Email")}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={onChange}
                  className={FIELD_CLASS}
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-ink-700 mb-1.5">
                {t("Subject")}
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                required
                value={form.subject}
                onChange={onChange}
                className={FIELD_CLASS}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-ink-700 mb-1.5">
                {t("Message")}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                value={form.message}
                onChange={onChange}
                className={FIELD_CLASS + " resize-none"}
              />
            </div>

            <button
              type="submit"
              disabled={status === "sending"}
              className="h-12 px-8 rounded-full bg-accent-500 text-white font-semibold
                hover:bg-accent-600 active:scale-[0.97] transition-[background-color,transform] duration-150
                disabled:opacity-60 disabled:cursor-not-allowed
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
            >
              {status === "sending" ? t("Sending...") : t("Send message")}
            </button>

            <div role="status" aria-live="polite" className="min-h-[1.5rem] pt-1">
              {status === "success" && (
                <p
                  ref={successRef}
                  className="flex items-center gap-2 text-sm font-medium text-green-700"
                >
                  <span
                    aria-hidden="true"
                    className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center"
                  >
                    ✓
                  </span>
                  {t("Message sent — we'll get back to you soon.")}
                </p>
              )}
              {status === "error" && (
                <p className="text-sm font-medium text-red-600">
                  {t("Something went wrong sending your message. Please email us directly at support@linkakam.com.")}
                </p>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
